import React, { useState, useEffect, useRef } from "react";
import { TextField, Select, Button } from "../ui";
import { FeatherChevronDown, FeatherLoader, FeatherX, FeatherPlus } from "@subframe/core";
import { getAllCustomers, getCustomerById } from "../services/customerApi";
import {
  SearchableDropdown,
  BankGlAccountDropdown,
} from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";

export interface SplitItem {
  id: string; // Unique ID for each split row
  from_account_id: string;
  amount: number;
  payment_mode: string;
}

interface CustomerDepositFromOtherAccountsSplitRecoProps {
  transactionDate: string;
  transactionAmount: number;
  bankAccountId?: string;
  onDateChange: (date: string) => void;
  onReceivedFromChange: (customerId: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  onSplitsChange: (splits: SplitItem[]) => void;
  zohoGl?: string;
}

const CustomerDepositFromOtherAccountsSplitReco: React.FC<
  CustomerDepositFromOtherAccountsSplitRecoProps
> = ({
  transactionDate,
  transactionAmount,
  bankAccountId,
  onDateChange,
  onReceivedFromChange,
  onReferenceNumberChange,
  onSplitsChange,
  zohoGl,
}) => {
  const [date, setDate] = useState<string>("");
  const [receivedFrom, setReceivedFrom] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>(
    "Select customer (optional)"
  );
  const [splits, setSplits] = useState<SplitItem[]>([
    { id: "1", from_account_id: "", amount: 0, payment_mode: "Cash" },
    { id: "2", from_account_id: "", amount: 0, payment_mode: "Cash" },
  ]);

  // Dropdown cache hook
  const { getCached, setCached } = useDropdownCache();
  const accountNamesRef = useRef<Map<string, string>>(new Map());

  // Cache for selected customers
  const [customersCache, setCustomersCache] = useState<Map<string, any>>(
    new Map()
  );

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  // Notify parent of splits changes
  useEffect(() => {
    onSplitsChange(splits);
  }, [splits, onSplitsChange]);

  // Calculate total amount
  const totalAmount = splits.reduce((sum, split) => sum + (split.amount || 0), 0);
  const remainingAmount = transactionAmount - totalAmount;
  const isTotalValid = Math.abs(remainingAmount) < 0.01; // Allow small floating point differences

  // Add new split row
  const handleAddRow = () => {
    const newId = String(Date.now());
    setSplits([
      ...splits,
      { id: newId, from_account_id: "", amount: 0, payment_mode: "Cash" },
    ]);
  };

  // Remove split row (minimum 2 rows)
  const handleRemoveRow = (id: string) => {
    if (splits.length > 2) {
      setSplits(splits.filter((split) => split.id !== id));
    }
  };

  // Update split field
  const updateSplit = (id: string, field: keyof SplitItem, value: any) => {
    setSplits(
      splits.map((split) =>
        split.id === id ? { ...split, [field]: value } : split
      )
    );
  };

  // Update account name for a split
  const updateAccountName = (accountId: string, accountName: string) => {
    accountNamesRef.current.set(accountId, accountName);
  };

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handleReceivedFromChange = (customer: any) => {
    const customerId = customer.zoho_customerid;
    setReceivedFrom(customerId);
    setSelectedCustomerName(customer.customer_name);
    onReceivedFromChange(customerId);

    // Cache the selected customer
    setCustomersCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(customerId, customer);
      return newCache;
    });

    const cacheKey = { searchTerm: "", limit: 20, offset: 0 };
    const cached = getCached<{
      items: any[];
      hasMore?: boolean;
    }>("customers", cacheKey);

    if (cached) {
      const hasCustomer = cached.items.some(
        (c: any) => c.zoho_customerid === customerId
      );
      if (!hasCustomer) {
        setCached("customers", cacheKey, {
          items: [customer, ...cached.items],
          hasMore: cached.hasMore,
        });
      }
    }
  };

  // Fetch customers async function
  const fetchCustomers = async (
    searchTerm: string,
    limit: number,
    offset: number,
    selectedCustomerId?: string,
    selectedCustomerName?: string
  ): Promise<{
    items: any[];
    hasMore?: boolean;
  }> => {
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { searchTerm, limit, offset };

    if (shouldCache) {
      const cached = getCached<{
        items: any[];
        hasMore?: boolean;
      }>("customers", cacheKey);

      if (cached) {
        if (offset === 0 && selectedCustomerId && selectedCustomerName) {
          const hasSelected = cached.items.some(
            (customer: any) => customer.zoho_customerid === selectedCustomerId
          );

          if (!hasSelected) {
            const cachedCustomer = customersCache.get(selectedCustomerId);
            if (cachedCustomer) {
              return {
                items: [cachedCustomer, ...cached.items],
                hasMore: cached.hasMore,
              };
            }

            try {
              const selectedResponse = await getCustomerById(selectedCustomerId);
              if (selectedResponse.data) {
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                return {
                  items: [mappedCustomer, ...cached.items],
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn("Could not fetch selected customer:", error);
            }

            const syntheticCustomer: any = {
              zoho_customerid: selectedCustomerId,
              customer_id: selectedCustomerId,
              customer_name: selectedCustomerName,
              company_name: selectedCustomerName,
              email: "",
              phone: "",
              status: "active",
              billing_address: null,
              shipping_address: null,
              currency_code: "",
              payment_terms: "",
              credit_limit: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            return {
              items: [syntheticCustomer, ...cached.items],
              hasMore: cached.hasMore,
            };
          }
        }
        return cached;
      }
    }

    try {
      const response = await getAllCustomers({
        search: searchTerm || undefined,
        limit,
        offset,
      });

      let items = response.data || [];

      if (offset === 0 && selectedCustomerId && selectedCustomerName) {
        const hasSelected = items.some(
          (customer: any) => customer.zoho_customerid === selectedCustomerId
        );

        if (!hasSelected) {
          const cachedCustomer = customersCache.get(selectedCustomerId);
          if (cachedCustomer) {
            items = [cachedCustomer, ...items];
          } else {
            try {
              const selectedResponse = await getCustomerById(selectedCustomerId);
              if (selectedResponse.data) {
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                items = [mappedCustomer, ...items];
              }
            } catch (error) {
              console.warn("Could not fetch selected customer:", error);
            }
          }
        }
      }

      const result = {
        items,
        hasMore: response.pagination?.has_more || false,
      };

      if (shouldCache) {
        setCached("customers", cacheKey, {
          items: response.data || [],
          hasMore: response.pagination?.has_more || false,
        });
      }

      return result;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return {
        items: [],
        hasMore: false,
      };
    }
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  const handleAccountChange = (splitId: string, account: GLMappingEntity) => {
    const accountIdStr = String(account.account_id);
    updateAccountName(accountIdStr, account.gl_name);
    updateSplit(splitId, "from_account_id", accountIdStr);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      {/* Global Fields */}
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received From
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchCustomers(
                  searchTerm,
                  limit,
                  offset,
                  receivedFrom,
                  selectedCustomerName !== "Select customer (optional)"
                    ? selectedCustomerName
                    : undefined
                )
              }
              trigger={
                <Button
                  className="w-full justify-between"
                  variant="neutral-secondary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedCustomerName}
                </Button>
              }
              getSearchableText={(customer) => customer.customer_name}
              renderItem={(customer) => customer.customer_name}
              getItemKey={(customer) => customer.zoho_customerid}
              onSelect={handleReceivedFromChange}
              selectedKey={receivedFrom}
              searchPlaceholder="Search customers..."
              emptyMessage="No customers found"
              noResultsMessage="No customers match your search"
              initialLimit={20}
            />
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Enter reference number (optional)"
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
      </div>

      {/* Split Payments Section */}
      <div className="flex w-full flex-col items-start gap-3">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-4 font-heading-4 text-default-font">
            Split Payments
          </span>
          <Button
            variant="neutral-secondary"
            size="small"
            onClick={handleAddRow}
          >
            <FeatherPlus className="mr-2 h-4 w-4" />
            Add Row
          </Button>
        </div>

        <div className="flex w-full flex-col gap-3">
          {splits.map((split, index) => (
            <div
              key={split.id}
              className="flex w-full items-start gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 p-4"
            >
              <div className="flex flex-1 items-center gap-3">
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    From Account <span className="text-error-600">*</span>
                  </span>
                  <BankGlAccountDropdown
                    preset="deposit_from_other"
                    bankAccountId={bankAccountId}
                    selectedAccountId={split.from_account_id}
                    selectedAccountName={
                      accountNamesRef.current.get(split.from_account_id) || ""
                    }
                    onSelect={(account) => handleAccountChange(split.id, account)}
                    placeholder="Select account"
                  />
                </div>
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Amount <span className="text-error-600">*</span>
                  </span>
                  <TextField className="w-full" label="" helpText="">
                    <TextField.Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={split.amount > 0 ? split.amount.toString() : ""}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateSplit(
                          split.id,
                          "amount",
                          parseFloat(event.target.value) || 0
                        )
                      }
                      onWheel={(event: React.WheelEvent<HTMLInputElement>) => {
                        event.currentTarget.blur();
                        event.preventDefault();
                      }}
                    />
                  </TextField>
                </div>
                <div className="flex flex-col items-start gap-2 flex-1">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Received Via <span className="text-error-600">*</span>
                  </span>
                  <Select
                    label=""
                    helpText=""
                    value={split.payment_mode}
                    onValueChange={(value) =>
                      updateSplit(split.id, "payment_mode", value)
                    }
                  >
                    <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
                    <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
                    <Select.Item value="Cash">Cash</Select.Item>
                    <Select.Item value="Cheque">Cheque</Select.Item>
                    <Select.Item value="Credit Card">Credit Card</Select.Item>
                    <Select.Item value="UPI">UPI</Select.Item>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => handleRemoveRow(split.id)}
                    disabled={splits.length <= 2}
                  >
                    <FeatherX className="h-4 w-4" />
                   
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Validation Summary */}
        <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-neutral-border bg-white p-4">
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Transaction Amount:
            </span>
            <span className="text-body-bold font-body-bold text-default-font">
              ₹{transactionAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Total Split Amount:
            </span>
            <span className="text-body-bold font-body-bold text-default-font">
              ₹{totalAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          <div className="flex w-full items-center justify-between">
            <span className="text-body font-body text-subtext-color">
              Remaining:
            </span>
            <span
              className={`text-body-bold font-body-bold ${
                isTotalValid
                  ? "text-success-600"
                  : remainingAmount > 0
                  ? "text-warning-600"
                  : "text-error-600"
              }`}
            >
              ₹{remainingAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
          {!isTotalValid && (
            <div className="mt-2 text-caption text-error-600">
              {remainingAmount > 0
                ? `Please add ₹${remainingAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} more to match the transaction amount.`
                : `Total split amount exceeds transaction amount by ₹${Math.abs(
                    remainingAmount
                  ).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}.`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDepositFromOtherAccountsSplitReco;
